// Registry/Revision-History section numbering, mirrored from the backend
// Word exporter (backend/src/services/exportService.js:
// WORD_EXCLUDED_SECTION_TITLES / deriveRegistrySectionNumbers /
// computeRevisionSectionNumber / isExcludedFromWord) so the browser preview
// and the exported Word document always agree on section numbers AND on
// which sections render at all.
//
// Why this needs to exist at all: a client SOP format can renumber or drop
// these sections entirely -- Pacific Life's 7-section format puts Gaps
// Register at 6.0 (not the generic format's 12.0), tracks Controls as an
// internal registry only (no Controls Registry section at all -- so there is
// no section number for Controls, not even a fallback one), and
// auto-appends Revision History at 7.0 (not 13.0). Before this fix the
// browser preview hardcoded the generic format's numbers (see git history on
// SOPContentRenderer.tsx / SOPGenerationTab.tsx), so a Pacific Life SOP's
// browser preview showed "see section 12" for a gap that lives in Section
// 6.0 in the same document's Word export, and a "13.0 Revision History"
// badge for a section the Word export numbers 7.0. A later pass fixed a
// second issue: the browser preview also had no equivalent of Word's
// isExcludedFromWord, so a hallucinated standalone "Controls Registry" or
// "Review and Approval" section (against prompt instructions, but not
// impossible) that Word suppresses could still render in the browser.
//
// If the backend's derivation logic ever changes, mirror the change here too
// -- there is no shared module between the two runtimes, so this is the one
// place in the frontend that must be kept in sync by hand.

export interface SOPSectionLike {
  section_number?: string;
  section_title?: string;
  title?: string;
  role?: string;
}

export interface RegistrySectionNums {
  // null when this SOP has no such section to point at. That was already the
  // rule for controls under a client format that tracks them internally (e.g.
  // Pacific Life); it now applies to both registries and to every format,
  // because a document can be missing a registry for a reason nobody chose.
  // See deriveRegistrySectionNumbers below.
  controls: string | null;
  gaps: string | null;
}

export interface DeriveRegistrySectionNumbersOptions {
  // Historic: true only for a client format that tracks controls as an
  // internal registry (Pacific-Life-shaped). It selected null over the 8
  // fallback; now that an absent section always resolves to null it changes
  // nothing. Kept so callers passing it still type-check.
  hasInternalControlsRegistry?: boolean;
}

// Matches ExportService.DEFAULT_REGISTRY_NUMS -- where the registries live in
// a COMPLETE generic SOP. No longer a fallback inside
// deriveRegistrySectionNumbers (see there); it remains the default argument
// for components rendering before any derivation has run.
export const DEFAULT_REGISTRY_SECTION_NUMS: { controls: string; gaps: string } = { controls: '8', gaps: '12' };

// Matches ExportService.WORD_EXCLUDED_SECTION_TITLES -- sections that are
// real, client-authored content but must never render as a standalone
// section, and must never count toward section numbering: a legacy Controls
// Registry SECTION (kept out of the Pacific Life count entirely -- it isn't
// a section there) and the auto-appended, unnumbered Review & Approval block
// (never a section in sopData.sections). If the model hallucinates a section
// with one of these titles despite prompt instructions forbidding it, Word
// already suppresses it (ExportService.isExcludedFromWord) -- this is the
// single shared list both the numbering logic below and
// isExcludedFromBrowserRender use, so the browser preview applies the exact
// same suppression instead of a separate title check.
const EXCLUDED_SECTION_TITLES = [
  'controls registry',
  'control registry',
  'review and approval',
  'review & approval',
];

/**
 * Matched by EXACT normalized title, never substring — the same rule
 * ExportService.isExcludedFromWord applies, and for the same reason it spells
 * out there: the standard 12-section format's Section 8.0 is titled "Quality
 * Control, Compliance Requirements and Controls Registry", which CONTAINS
 * "controls registry" without being one. Substring matching hid that whole
 * section from the browser preview while Word rendered it, so the on-screen
 * SOP jumped 7.0 → 9.0 and its C-XX cross-references pointed at a section the
 * reader could not see. The list targets a standalone hallucinated registry
 * section (and the unnumbered Review & Approval block), not a real section
 * whose title happens to name one.
 */
const isExcludedTitle = (title?: string): boolean => {
  // trim() runs BEFORE the punctuation strip: "Controls Registry:  " ends in a
  // space, so [:.]+$ would not see the colon and the title would not match.
  const exact = (title || '').toLowerCase().replace(/\s+/g, ' ').trim().replace(/[:.]+$/, '').trim();
  return EXCLUDED_SECTION_TITLES.some((ex) => exact === ex);
};

/**
 * Should this section be hidden from the browser preview entirely?
 *
 * Mirrors ExportService.isExcludedFromWord's WORD_EXCLUDED_SECTION_TITLES
 * check (the AI-analysis-artifact half of isExcludedFromWord --
 * EXCLUDED_SECTION_TITLES in exportService.js, e.g. "compliance assessment"
 * -- has no browser equivalent because those fields are never inside
 * sopData.sections in the first place; see mergeSOPParts in
 * enhancedSOPService.js). Call this once per section in the render loop, the
 * same way SOPGenerationTab.tsx now does, rather than duplicating a title
 * check in each component that lists sections.
 */
export function isExcludedFromBrowserRender(title?: string): boolean {
  return isExcludedTitle(title);
}

/**
 * Where do this SOP's Controls Registry and Gaps Register actually live?
 *
 * Matches role first (client SOP formats tag a section 'controls_registry' /
 * 'gaps_register' explicitly), falling back to a title match. A registry that
 * is not among the sections resolves to null, for controls and gaps alike.
 *
 * The 8/12 defaults used to fill in whenever nothing was found, on the
 * assumption that a document always carries the registries its own tags cite.
 * Production data disproved that: Call 1 generates every structure section in
 * one reply, and when that reply was cut off the tail of the template -- 8.0
 * Controls Registry and 12.0 Gaps Register -- was dropped silently. 38 of 106
 * generic-format SOPs had no Gaps Register section at all, and their preview
 * and export both read "see section 12" for a document whose sections stopped
 * at 8.0 Revision History. A reference with no pointer is imprecise; one that
 * names a section the reader cannot find is wrong.
 *
 * Returns display numbers with the trailing ".0" stripped ("6", not "6.0"),
 * matching the "— see section 6" inline phrasing.
 */
export function deriveRegistrySectionNumbers(
  sections: SOPSectionLike[] = [],
  // Accepted so existing call sites are unchanged, but no longer consulted:
  // it only ever chose between null and the 8 fallback, and there is no
  // longer a fallback to choose. Mirrors the backend, which dropped the
  // option from its own signature.
  _options: DeriveRegistrySectionNumbersOptions = {}
): RegistrySectionNums {
  const display = (s: SOPSectionLike): string | null => {
    const raw = String(s.section_number || '').trim();
    return raw ? raw.replace(/\.0$/, '') : null;
  };

  let controls: string | null = null;
  let gaps: string | null = null;
  (sections || []).forEach((s) => {
    const title = ((s.section_title || s.title) || '').toLowerCase();
    if (!controls && (s.role === 'controls_registry' || /controls?\s+regist(er|ry)/.test(title))) {
      controls = display(s);
    }
    if (!gaps && (s.role === 'gaps_register' || /gaps?\s*regist(er|ry)/.test(title))) {
      gaps = display(s);
    }
  });

  return {
    controls: controls || null,
    gaps: gaps || null,
  };
}

/**
 * The auto-appended Revision History section must number one past the LAST
 * section that actually renders -- not sections.length + 1, since the array
 * may hold sections excluded from numbering (see EXCLUDED_SECTION_TITLES
 * above). Prefers each section's real section_number when present, matching
 * exportService.js's computeRevisionSectionNumber exactly.
 */
export function computeRevisionSectionNumber(sections: SOPSectionLike[] = []): string {
  const rendered = (sections || []).filter((s) => {
    const title = (s.section_title || s.title || '').trim();
    return !isExcludedTitle(title);
  });

  let maxNum = 0;
  rendered.forEach((s) => {
    const raw = String(s.section_number || '').trim();
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n > maxNum) maxNum = n;
  });
  const next = maxNum > 0 ? maxNum + 1 : rendered.length + 1;
  return `${next}.0`;
}
